import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import SecretBox from "../../components/SecretBox";
import Loader, { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { createStaff, deleteStaff, getStaff, resetStaffPassword, updateStaff } from "../../services/staff";

// What a store owner can let staff do. Each staff member gets only the boxes the owner ticks.
export const STAFF_AREAS = [
  { id: "content", label: "Edit website", hint: "Banners, categories, products, offers and policies", icon: "✎" },
  { id: "orders", label: "Manage orders", hint: "See orders and update their status", icon: "📦" },
  { id: "customers", label: "Shoppers & reviews", hint: "The store's shopper accounts and reviews", icon: "👥" },
  { id: "settings", label: "Store settings", hint: "Shipping, GST, payment and business details", icon: "⚙" },
];
const areaLabel = (id) => (STAFF_AREAS.find((a) => a.id === id) || {}).label || id;

function StaffModal({ member, onClose, onSaved, notify }) {
  const editing = Boolean(member);
  const [form, setForm] = useState({
    name: member ? member.name : "",
    email: member ? member.email || "" : "",
    phone: member ? member.phone || "" : "",
    permissions: member ? member.permissions : ["orders"],
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (id) => setForm((f) => ({ ...f, permissions: f.permissions.includes(id) ? f.permissions.filter((p) => p !== id) : [...f.permissions, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim() && !form.phone.trim()) return setError("Enter an email or a mobile number they will log in with.");
    if (!form.permissions.length) return setError("Tick at least one thing this person can do.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), permissions: form.permissions };
      if (editing) {
        await updateStaff(member.id, payload);
        notify(`${payload.name} updated`);
        onSaved(null);
      } else {
        const { data } = await createStaff(payload);
        onSaved({ title: `Staff added: ${data.staff.name}`, login: data.staff.email || data.staff.phone, password: data.temporaryPassword });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="STAFF" title={editing ? `Edit ${member.name}` : "Add staff member"} onClose={onClose}
      description={editing ? undefined : "They get a temporary password and choose their own at first login."}>
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Full name<input required minLength={2} value={form.name} onChange={set("name")} /></label>
        <div className="form-row">
          <label className="admin-field">Email<input type="email" value={form.email} onChange={set("email")} /></label>
          <label className="admin-field">Mobile number<input type="tel" value={form.phone} onChange={set("phone")} /></label>
        </div>
        <div className="staff-areas-label">What can they do?</div>
        <div className="staff-areas">
          {STAFF_AREAS.map((area) => (
            <label key={area.id} className={`staff-area ${form.permissions.includes(area.id) ? "is-on" : ""}`}>
              <input type="checkbox" checked={form.permissions.includes(area.id)} onChange={() => toggle(area.id)} />
              <span className="staff-area-icon" aria-hidden="true">{area.icon}</span>
              <span><b>{area.label}</b><small>{area.hint}</small></span>
            </label>
          ))}
        </div>
        <p className="muted">Staff never see billing, payments or your other staff.</p>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : editing ? "Save changes" : "Add staff"}</button>
        </div>
      </form>
    </Modal>
  );
}

// Store owner: the people who help run their websites, and what each of them can do
export default function StaffTab({ notify }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // 'new' or a member being edited
  const [removing, setRemoving] = useState(null);
  const [secret, setSecret] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getStaff();
      setStaff(data.staff);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (m) => {
    try {
      await updateStaff(m.id, { status: m.status === "active" ? "inactive" : "active" });
      notify(`${m.name} ${m.status === "active" ? "paused" : "can log in again"}`);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  const resetPassword = async (m) => {
    try {
      const { data } = await resetStaffPassword(m.id);
      setSecret({ title: `New password for ${m.name}`, login: m.email || m.phone, password: data.temporaryPassword });
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  const remove = async () => {
    try {
      const { data } = await deleteStaff(removing.id);
      notify(data.message);
      setRemoving(null);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">YOUR TEAM</p>
          <h1>Staff</h1>
          <p className="subheading">Let people help run your store. Each person logs in with their own account and sees only what you allow.</p>
        </div>
        <button className="primary-btn" onClick={() => setModal("new")}>＋ Add staff</button>
      </div>

      {secret && <SecretBox {...secret} onDone={() => setSecret(null)} />}
      {error && <div className="admin-error">{error}</div>}
      {loading && !staff.length && <Loader label="Loading staff" />}
      {!loading && !staff.length && !error && (
        <div className="panel staff-empty">
          <span aria-hidden="true">👥</span>
          <b>No staff yet</b>
          <p>Add someone to help with orders, products or settings. You choose exactly what they can do.</p>
          <button className="primary-btn" onClick={() => setModal("new")}>＋ Add your first staff member</button>
        </div>
      )}

      <div className="staff-grid">
        {staff.map((m) => (
          <article className={`staff-card ${m.status === "active" ? "" : "is-paused"}`} key={m.id}>
            <div className="staff-card-top">
              <span className="staff-avatar" aria-hidden="true">{m.name.charAt(0).toUpperCase()}</span>
              <div><b>{m.name}</b><small>{m.email || m.phone}</small></div>
              <span className={`status-pill ${m.status === "active" ? "status-on" : "status-off"}`}><i />{m.status === "active" ? "Active" : "Paused"}</span>
            </div>
            <div className="staff-perms">
              {m.permissions.map((p) => <span key={p}>{areaLabel(p)}</span>)}
            </div>
            <div className="row-actions staff-actions">
              <button className="edit-action" onClick={() => setModal(m)}>Edit access</button>
              <button className="toggle-action" onClick={() => toggleStatus(m)}>{m.status === "active" ? "Pause" : "Resume"}</button>
              <button className="toggle-action" onClick={() => resetPassword(m)}>Reset password</button>
              <button className="delete-action" onClick={() => setRemoving(m)}>Remove</button>
            </div>
          </article>
        ))}
      </div>

      {modal && (
        <StaffModal member={modal === "new" ? null : modal} notify={notify} onClose={() => setModal(null)}
          onSaved={(created) => { setModal(null); if (created) setSecret(created); load(); }} />
      )}
      {removing && (
        <Modal eyebrow="REMOVE STAFF" title={`Remove ${removing.name}?`} onClose={() => setRemoving(null)}>
          <p className="muted">They are signed out at once and can no longer log in. You can add them again later.</p>
          <div className="product-modal-actions">
            <button className="outline-btn" onClick={() => setRemoving(null)}>Cancel</button>
            <button className="danger-btn" onClick={remove}>Remove</button>
          </div>
        </Modal>
      )}
    </>
  );
}
